import {
  Component,
  Custom,
  Dropdown,
  Image as _Image,
  Video as _Video,
  MultiField,
  Number,
  OptionEditor,
  Product,
  Products,
  Radio as _Radio,
  RichText,
  Section,
  Screen,
  Screens,
  ShippingOptionsEditor,
  Switch as _Switch,
  Textfield,
} from '../formFields'

import {
  TOOLTIPS,
  ALIGNMENT_OPTIONS,
  alignment,
  verticalAlignment,
  eventType,
  webhookUrl,
  data,
  delay,
  toastVariant,
  toastMessage,
  direction,
  HEADING_LEVELS,
  BUTTON_VARIANTS,
} from './_common'

const CONTAINER_COLUMNS = [
  {
    label: '1 Column',
    value: 1,
  },
  {
    label: '2 Columns',
    value: 2,
  },
  {
    label: '3 Columns',
    value: 3,
  },
  {
    label: '4 Columns',
    value: 4,
  },
  {
    label: '5 Columns',
    value: 5,
  },
]

const SELECTION_COLUMNS = [
  {
    label: '1 Column',
    value: 1,
  },
  {
    label: '2 Columns',
    value: 2,
  },
  {
    label: '3 Columns',
    value: 3,
  },
  {
    label: '4 Columns',
    value: 4,
  },
]

const DISPLAY_OPTIONS = [
  { label: 'Paragraph', value: 'p' },
  ...HEADING_LEVELS,
  { label: 'Image', value: 'img' },
  { label: 'Video', value: 'video' },
  { label: 'Stock Status', value: 'stockStatus' },
]

const RSS_LINK_TYPE_OPTIONS = [
  { label: 'Open in custom screen', value: 'screen' },
  { label: 'Do nothing', value: '' },
  { label: 'Open original article', value: 'article' },
  { label: 'Follow custom URL', value: 'link' },
]

const RSS_PROPERTY_OPTIONS = [
  { label: 'Image', value: 'image' },
  { label: 'Thumbnail', value: 'thumbnail' },
  { label: 'Title', value: 'title' },
  { label: 'Description', value: 'description' },
  { label: 'Content', value: 'content' },
  { label: 'Author', value: 'creator' },
  { label: 'Date', value: 'date' },
  { label: 'Link', value: 'link' },
  { label: 'Category', value: 'category' },
]

const DATE_FORMAT_OPTIONS = [
  { label: 'No formatting', value: '' },
  { label: 'Hidden', value: 'hidden' },
  { label: 'Local', value: 'local' },
  { label: 'ISO', value: 'ISO' },
  { label: 'MM-DD', value: 'MM-DD' },
  { label: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
  { label: 'DD/MM/YYYY', value: 'DD/MM/YYYY' },
  { label: 'DD-MM-YYYY', value: 'DD-MM-YYYY' },
  { label: 'YYYY-MM-DD', value: 'YYYY-MM-DD' },
]

const TIME_FORMAT_OPTIONS = [
  { label: 'Hidden', value: 'hidden' },
  { label: 'Local', value: 'local' },
  { label: 'Hours (12h)', value: 'hh' },
  { label: 'Hours (24h)', value: 'HH' },
  { label: 'Hours, minutes (12h)', value: 'hh:mm' },
  { label: 'Hours, minutes (24h)', value: 'HH:mm' },
  { label: 'Hours, minutes, seconds (12h)', value: 'hh:mm:ss' },
  { label: 'Hours, minutes, seconds (24h)', value: 'HH:mm:ss:' },
]

// Common fields, to reduce code in each component's fields
const title = Textfield({ name: 'title', label: 'Title' })
const subtitle = RichText({ name: 'subtitle', label: 'Subtitle' })
const pretitle = Textfield({
  name: 'pretitle',
  label: 'Pretitle',
  tooltip: 'Small text above main title, also known as kicker.',
})
const text = RichText({ name: 'text', label: 'Text' })
const label = Textfield({ name: 'label', label: 'Label', tooltip: TOOLTIPS.LABEL })
const link = Screen({
  name: 'link',
  label: 'Link to',
  filter: null,
  allowExternal: true,
  allowProduct: true,
  tooltip: TOOLTIPS.LINK,
})
const isFullWidth = _Switch({
  name: 'isFullWidth',
  label: 'Full width',
  defaultChecked: false,
  tooltip:
    'Make the component fill the whole window width, extending beyond usual content column. Only works in top-level (not nested) components.',
})
const formId = Textfield({
  name: 'formId',
  label: 'Form Id',
  tooltip: TOOLTIPS.FORM_ID,
})

const name = Textfield({
  name: 'name',
  label: 'Field Name',
  tooltip: TOOLTIPS.NAME,
})
const path = Textfield({
  name: 'path',
  label: 'Data storage path',
  tooltip: TOOLTIPS.PATH,
})
const isRequired = _Switch({
  name: 'isRequired',
  label: 'Is required',
  defaultChecked: false,
  tooltip: 'Visually mark field as required.',
})
const preFill = _Switch({
  name: 'preFill',
  label: 'Pre-fill if possible',
  defaultChecked: true,
  tooltip: TOOLTIPS.PRE_FILL,
})
const defaultValue = Textfield({
  name: 'defaultValue',
  label: 'Fixed default value',
  tooltip: TOOLTIPS.DEFAULT_VALUE,
})
const placeholder = Textfield({ name: 'placeholder', label: 'Placeholder' })

const productId = Product({
  name: 'productId',
  label: 'Product',
  filter: null,
  emptyOptionLabel: 'Inherit from screen',
})

const textAlignment = Dropdown({
  name: 'textAlignment',
  label: 'Text Alignment',
  options: ALIGNMENT_OPTIONS,
  filter: null,
  tooltip: 'Text block alignment within the component.',
})

const buttonLabel = Textfield({ name: 'buttonLabel', label: 'Label' })
const buttonLink = {
  ...link,
  name: 'buttonLink',
}
const buttonVariant = Dropdown({
  name: 'buttonVariant',
  label: 'Variant',
  options: BUTTON_VARIANTS,
  filter: null,
})
const buttonIsQuiet = _Switch({ name: 'buttonIsQuiet', label: 'Quiet (no border)', defaultChecked: false })
const buttonEventType = Textfield({
  name: 'buttonEventType',
  label: 'Event Type',
  tooltip: TOOLTIPS.EVENT_TYPE,
})
const buttonWebhookUrl = Textfield({
  name: 'buttonWebhookUrl',
  label: 'Webhook URL',
  tooltip: TOOLTIPS.WEBHOOK,
})
const buttonFormId = Textfield({
  name: 'buttonFormId',
  label: 'Form Id',
  tooltip: TOOLTIPS.FORM_ID,
})
const buttonData = OptionEditor({
  name: 'buttonData',
  label: 'Custom Data',
  columns: [
    { name: 'label', label: 'Property Name/Path', isKey: true },
    { name: 'valueType', label: 'Type', type: 'fieldType' },
    { name: 'value', label: 'Value', type: 'dynamic', typeColumn: 'valueType' },
  ],
  saveAs: 'array',
  tooltip: TOOLTIPS.DATA,
})

const buttonAppearance = Section('Button Appearance', buttonLabel, buttonVariant, buttonIsQuiet)

const buttonActions = Section(
  'Button Actions',
  buttonLink,
  buttonEventType,
  buttonWebhookUrl,
  buttonFormId,
  buttonData,
  toastVariant,
  toastMessage
)

const productCardVariants = Dropdown({
  name: 'variant',
  label: 'Variant',
  options: [
    { label: 'Vertical', value: 'vertical' },
    { label: 'Horizontal Left', value: 'horizontalLeft' },
    { label: 'Horizontal Right', value: 'horizontalRight' },
  ],
  hidden: null,
  filter: null,
})

const selectionFields = [
  {
    ...direction,
    hidden: (cmp) => ['Card', 'Picker', 'Switch', 'ComboBox'].includes(cmp.content.type),
  },
  Dropdown({
    name: 'columns',
    label: 'Card Columns',
    options: SELECTION_COLUMNS,
    filter: null,
    hidden: (cmp) => cmp.content.type !== 'Card',
  }),
  OptionEditor({
    name: 'options',
    label: 'Options',
    hidden: (cmp) => cmp.content.type === 'Switch',
    columns: [
      { label: 'Label', name: 'label' },
      { label: 'Value', name: 'value' },
      {
        label: 'Additional Data',
        name: 'data',
        tooltip:
          'Additional properties to set in dataLayer for this option. Should follow JSON Object syntax: { "path1": " value1", "path2": "value2"}',
      },
    ],
  }),
  isRequired,
  Section(
    'Data Handling',
    name,
    path,
    formId,
    defaultValue,
    preFill,
    _Switch({
      name: 'shouldSaveSeparately',
      label: 'Save options separately',
      tooltip:
        'By default (not checked) selected options are saved at *path* as an array. If checked, each selected option will be saved at *path*.*value* as true/false.',
      defaultChecked: false,
      hidden: (cmp) => ['Card', 'Picker', 'Switch', 'ComboBox'].includes(cmp.content.type),
    })
  ),
  Section('Alignment', alignment),
]

const rssMappingInput = OptionEditor({
  name: 'mapping',
  label: 'Content Mapping',
  columns: [
    { label: 'Tag name', name: 'from' },
    { label: 'Use as', name: 'to', type: 'dropdown', options: RSS_PROPERTY_OPTIONS },
    {
      label: 'Keep Array',
      name: 'keepArray',
      type: 'boolean',
      tooltip:
        'Set to true if you expect more than one tag with this name. You need to then reference the desired item with Selector, eg. ".0" for the first item, ".1" for the second, etc. ',
    },
    {
      label: 'Selector',
      name: 'selector',
      tooltip:
        'Specifies how to access desired value if it is stored not in tag content but its attribute, or there are multiple tags. Eg. you can get url of <media:content url="..."> with ".$.url" selector.',
    },
  ],
})

// Actual components

export const Banner = Component('Banner', {
  description: 'Displays an image with optional texts and button. Can link to other screen or trigger actions.',
  fields: [
    _Image({ name: 'image', label: 'Image' }),
    isFullWidth,
    link,
    Section('Texts', title, subtitle, pretitle),
    buttonAppearance,
    buttonActions,
    Section('Alignment', alignment, verticalAlignment),
  ],
  exposedStyles: [
    { label: 'Height', name: 'height', placeholder: 'eg. auto or 500px' },
    { label: 'Text color', name: 'color', placeholder: 'eg. #f9e322 or rgb(255,123,0)' },
  ],
  defaultContent: {
    content: {
      image: '',
      alignment: 'center',
    },
  },
})

export const Breadcrumb = Component('Breadcrumb', {
  description: 'Displays a navigational breadcrumb',
  fields: [link, text, isFullWidth],
  defaultContent: {
    content: { text: 'Home / ${product.category} / ${product.name}', link: 'home' },
  },
})

export const Button = Component('Button', {
  description:
    'Displays a button with specified label and visual variant. Can link to other screen or trigger actions.',
  fields: [
    label,
    link,
    Dropdown({ name: 'variant', label: 'Variant', options: BUTTON_VARIANTS, filter: null }),
    _Switch({ name: 'isQuiet', label: 'Is quiet', defaultChecked: false }),
    Section(
      'Actions',
      eventType,
      webhookUrl,
      formId,
      data,
      {
        ...delay,
        hidden: (cmp) => {
          return cmp.content.variant === 'link'
        },
      },
      { ...toastVariant, hidden: (cmp) => cmp.content.variant === 'link' },
      { ...toastMessage, hidden: (cmp) => cmp.content.variant === 'link' }
    ),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: {
      label: 'Click me',
      variant: 'cta',
      alignment: 'center',
    },
  },
})

export const ButtonLink = Component('ButtonLink', {
  label: 'Link',
  description: 'Displays a simple link to other screen/URL or trigger actions.',
  fields: [label, link, Section('Actions', eventType, webhookUrl, formId, data), Section('Alignment', alignment)],
  defaultContent: {
    content: {
      label: 'Click me',
      alignment: 'left',
    },
  },
})

export const Card = Component('Card', {
  description: 'Displays image with optional title, subtitle and button.',
  fields: [
    _Image({ name: 'image', label: 'Image' }),
    link,
    Dropdown({
      name: 'variant',
      label: 'Layout',
      options: [
        { label: 'Image on top', value: 'vertical' },
        { label: 'Image on the left', value: 'horizontalLeft' },
        { label: 'Image on the right', value: 'horizontalRight' },
        { label: 'Image partially overlayed', value: 'partialOverlay' },
        { label: 'Image fully overlayed', value: 'fullOverlay' },
      ],
      hidden: null,
      filter: null,
    }),
    Dropdown({ name: 'titleLevel', label: 'Title size', options: HEADING_LEVELS, filter: null }),
    _Switch({
      name: 'selectable',
      label: 'Selectable',
      hidden: null,
      defaultChecked: false,
    }),
    Section('Texts', title, subtitle, pretitle),
    buttonAppearance,
    buttonActions,
    Section('Alignment', alignment, verticalAlignment),
  ],
  defaultContent: {
    content: { title: 'Lorem Ipsum', titleLevel: 2, selectable: false },
  },
})

export const CartContent = Component('CartContent', {
  description: 'Displays the products that are currently in cart',
  category: 'Cart & Checkout',
  fields: [
    _Switch({ name: 'allowEdit', label: 'Allow editing', defaultChecked: true }),
    Textfield({
      name: 'nameLabel',
      label: 'Name Label',
    }),
    Textfield({
      name: 'priceLabel',
      label: 'Price label',
    }),
    Textfield({
      name: 'quantityLabel',
      label: 'Quantity label',
    }),
  ],
  defaultContent: {
    content: { allowEdit: true, nameLabel: 'Name', priceLabel: 'Price', quantityLabel: 'Qty' },
  },
})

export const CartPriceSummary = Component('CartPriceSummary', {
  description: 'Displays value of items added to cart, as well as applied discounts, shipping cost and total price.',
  category: 'Cart & Checkout',
  fields: [
    Textfield({
      name: 'subtotalLabel',
      label: 'Subtotal label',
    }),
    Textfield({
      name: 'shippingLabel',
      label: 'Shipping label',
    }),
    Textfield({
      name: 'discountLabel',
      label: 'Discount label',
    }),
    Textfield({
      name: 'totalLabel',
      label: 'Total label',
    }),
  ],
  defaultContent: {
    content: { subtotalLabel: 'Subtotal', shippingLabel: 'Shipping', discountLabel: 'Discounts', totalLabel: 'Total' },
  },
})

export const CountryPicker = Component('CountryPicker', {
  description: 'Displays a dropdown list of all countries',
  category: 'Form',
  fields: [label, Section('Data Handling', name, path, formId, defaultValue, preFill)],
  defaultContent: { content: { type: 'CountryPicker', label: 'Country' } },
})

export const Checkbox = Component('Checkbox', {
  description: 'Displays one ore more options as checkboxes.',
  category: 'Form',
  fields: [
    label,
    isRequired,
    OptionEditor({
      name: 'options',
      label: 'Options',
      hidden: (cmp) => cmp.content.type === 'Switch',
      columns: [
        { label: 'Label', name: 'label' },
        { label: 'Value', name: 'value' },
        {
          label: 'Default state',
          name: 'defaultChecked',
          type: 'dropdown',
          options: [
            { label: 'Checked', value: 'true' },
            { label: 'Unchecked', value: 'false' },
          ],
        },
        {
          label: 'Additional Data',
          name: 'data',
          tooltip:
            'Additional properties to set in dataLayer for this option. Should follow JSON Object syntax: { "path1": " value1", "path2": "value2"}',
        },
      ],
    }),
    Section(
      'Data Handling',
      name,
      path,
      formId,
      defaultValue,
      preFill,
      _Switch({
        name: 'shouldSaveSeparately',
        label: 'Save options separately',
        tooltip:
          'By default (not checked) selected options are saved at *path* as an array. If checked, each selected option will be saved at *path*.*value* as true/false.',
        defaultChecked: false,
        hidden: (cmp) => ['Card', 'Picker', 'Switch', 'ComboBox'].includes(cmp.content.type),
      })
    ),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: { type: 'Checkbox', direction: 'vertical', alignment: 'left', preFill: true, shouldSaveSeparately: false },
  },
})

export const ComboBox = Component('ComboBox', {
  description: 'Displays one ore more options drop-down picker with custom input option.',
  category: 'Form',
  fields: [label, placeholder, ...selectionFields],
  defaultContent: {
    content: { type: 'ComboBox', direction: 'vertical', alignment: 'left', preFill: true },
  },
})

export const Configurator = Component('Configurator', {
  description:
    'Configurator that allows you to select one option per defined category and displays a preview image. Example: Carvelo car configurator.',
  fields: [
    OptionEditor({
      name: 'options',
      label: 'Options',
      columns: [
        {
          name: 'category',
          label: 'Category*',
          tooltip:
            'Category used to group options. If all options have the same category (empty value counts), category picker will not be displayed. If there are categories provided, options with empty category will be omitted.',
        },
        { name: 'name', label: 'Name' },
        { name: 'description', label: 'Description' },
        { name: 'image', label: 'Image*' },
        { name: 'thumbnail', label: 'Thumbnail', tooltip: 'Optional, image will be used as fallback.' },
      ],
    }),
    path,
    formId,
  ],
  exposedStyles: [{ label: 'Height', name: 'height', placeholder: 'eg. 500px or auto' }],
  defaultContent: {
    content: {
      options: [
        {
          category: 'Exterior',
          name: 'White',
          description: '$1500',
          image: 'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/car-configurator/model-a-white.jpeg',
          thumbnail: '#fff',
        },
        {
          category: 'Exterior',
          name: 'Silver',
          description: '$1500',
          image: 'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/car-configurator/model-a-silver.jpeg',
          thumbnail: 'rgb(241, 241, 241)',
        },
        {
          category: 'Exterior',
          name: 'Red',
          description: '$2500',
          image: 'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/car-configurator/model-a-red.jpeg',
          thumbnail: 'rgb(166, 41, 36)',
        },
        {
          category: 'Interior',
          name: 'Black',
          description: '$1500',
          image:
            'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/car-configurator/model-a-interior-black.jpeg',
          thumbnail: '#000',
        },
        {
          category: 'Interior',
          name: 'Brown',
          description: '$2000',
          image:
            'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/car-configurator/model-a-interior-brown.jpeg',
          thumbnail: 'rgb(82, 67, 63)',
        },
        {
          category: 'Interior',
          name: 'Red',
          description: '$2000',
          image:
            'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/car-configurator/model-a-interior-red.jpeg',
          thumbnail: 'rgb(199, 74, 56)',
        },
        {
          category: 'Interior',
          name: 'Orange',
          description: '$2500',
          image:
            'https://demo-system-next.s3.eu-north-1.amazonaws.com/assets/car-configurator/model-a-interior-orange.jpeg',
          thumbnail: 'rgb(235, 140, 75)',
        },
      ],
    },
  },
})

export const ConfirmOrderButton = Component('ConfirmOrderButton', {
  description: 'Button with extra logic for placing order.',
  category: 'Cart & Checkout',
  fields: [
    label,
    link,
    Dropdown({ name: 'variant', label: 'Variant', options: BUTTON_VARIANTS, filter: null }),
    Section('Actions', eventType, webhookUrl, formId, data, toastMessage, toastVariant),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: {
      label: 'Confirm order',
      variant: 'cta',
      alignment: 'right',
    },
  },
})

export const Contact = Component('Contact', {
  label: 'Contact Form',
  description: 'Displays contact form with email, optional topic, message and submit button.',
  fields: [
    Textfield({ name: 'emailLabel', label: 'Email Label' }),
    Textfield({ name: 'emailPath', label: 'Email Path', tooltip: TOOLTIPS.PATH }),
    Dropdown({
      name: 'topicInput',
      label: 'Topic Input',
      options: [
        { label: 'Textfield', value: 'textfield' },
        { label: 'Picker', value: 'picker' },
        { label: 'Hidden', value: 'hidden' },
      ],
      filter: null,
    }),
    Textfield({ name: 'topicLabel', label: 'Topic Label', hidden: (cmp) => cmp.content.topicInput === 'hidden' }),
    Textfield({
      name: 'topicPath',
      label: 'Topic Path',
      tooltip: TOOLTIPS.PATH,
      hidden: (cmp) => cmp.content.topicInput === 'hidden',
    }),
    OptionEditor({
      name: 'topicOptions',
      label: 'Topic Options',
      columns: [
        { label: 'Label', name: 'label' },
        { label: 'Value', name: 'value' },
      ],
      hidden: (cmp) => cmp.content.topicInput !== 'picker',
    }),
    Textfield({ name: 'messageLabel', label: 'Message Label' }),
    Textfield({ name: 'messagePath', label: 'Message Path', tooltip: TOOLTIPS.PATH }),
    buttonAppearance,
    buttonActions,
    Section('Alignment', alignment),
  ],
  exposedStyles: [{ label: 'Gap', name: 'gap', placeholder: 'eg. 1rem or 8px' }],
  defaultContent: {
    content: {
      emailLabel: 'Email',
      emailPath: 'personalEmail.address',
      topicInput: 'textfield',
      topicLabel: 'Topic',
      topicPath: 'contact.topic',
      messageLabel: 'Message',
      messagePath: 'contact.message',
      buttonLabel: 'Submit',
      buttonToastVariant: 'success',
      buttonToastMessage: 'Thank you for contacting us.',
      alignment: 'center',
    },
    styles: {
      gap: '1rem',
    },
  },
})

export const Container = Component('Container', {
  label: 'Column Container',
  description:
    'Responsive layout container with up to 5 columns. Can also be used as one column to create a visual section.',
  category: 'Layout',
  fields: [
    Dropdown({
      name: '_columns',
      label: 'Columns',
      options: CONTAINER_COLUMNS,
      hidden: null,
      filter: null,
      tooltip: 'Number of columns. You can use empty columns for asymmetric layout.',
    }),
    Textfield({
      name: 'layout',
      label: 'Column layout',
      hidden: null,
      tooltip:
        'Space-separated list of columns widths. Values should be valid CSS widths with units, eg. 25%, 200px, 6rem, etc. Columns with missing values will try to fill remaining space.',
    }),
    isFullWidth,
    _Switch({ name: 'wrap', label: 'Wrap overflowing columns', defaultChecked: false }),
  ],
  exposedStyles: [
    { label: 'Column gap', name: 'columnGap', placeholder: 'eg. 2rem or 16px' },
    { label: 'Row gap', name: 'rowGap', placeholder: 'eg. 1rem or 8px' },
    {
      label: 'Background',
      name: 'background',
      tooltip: "Color, gradient or image, eg: #fab31e, var(--primaryColor) or url('https://dsn.adobe.com/image.jpg')",
    },
    {
      label: 'Background Repeat',
      name: 'backgroundRepeat',
      tooltip:
        'If background is an image, should it be displayed in a repeating pattern or not. Available options: repeat, no-repeat, repeat-x, repeat-y, space, round.',
    },
    {
      label: 'Background Size',
      name: 'backgroundSize',
      tooltip:
        'If background is an image, should it be resized to match size. Available options: "cover", "contain", or values for each axis, eg. "50% 100%", "100% 150px", "300px 200px", etc.',
    },
  ],
  hasColumns: true,
  defaultContent: {
    meta: {
      _columns: 1,
      layout: '',
      isFullWidth: false,
    },
    content: [[]],
    styles: { columnGap: '2rem', rowGap: '1rem' },
  },
})

export const DateField = Component('DateField', {
  description: 'Displays a date input field. More suitable for past dates (birthdays) than Date Picker',
  category: 'Form',
  fields: [
    label,
    Dropdown({
      name: 'format',
      label: 'Save Format',
      options: [
        { label: 'AEP date (YYYY-MM-DD)', value: 'YYYY-MM-DD' },
        { label: 'AEP datetime (ISO)', value: 'ISO' },
        { label: 'US (MM/DD/YYYY)', value: 'MM/DD/YYYY' },
        { label: 'UK (DD/MM/YYYY)', value: 'DD/MM/YYYY' },
        { label: 'EU (DD-MM-YYYY)', value: 'DD-MM-YYYY' },
        { label: 'Based on locale', value: 'locale' },
      ],
      filter: null,
    }),
    isRequired,
    Section('Data Handling', name, path, formId, defaultValue, preFill),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: { type: 'text', format: 'YYYY-MM-DD', isRequired: false, preFill: true, alignment: 'left' },
  },
})

export const DatePicker = Component('DatePicker', {
  description: 'Displays a date picker field. More suitable for close dates. For birthdays, etc. prefer Date Field.',
  category: 'Form',
  fields: [
    label,
    Dropdown({
      name: 'format',
      label: 'Save Format',
      options: [
        { label: 'AEP date (YYYY-MM-DD)', value: 'YYYY-MM-DD' },
        { label: 'AEP datetime (ISO)', value: 'ISO' },
        { label: 'US (MM/DD/YYYY)', value: 'MM/DD/YYYY' },
        { label: 'UK (DD/MM/YYYY)', value: 'DD/MM/YYYY' },
        { label: 'EU (DD-MM-YYYY)', value: 'DD-MM-YYYY' },
        { label: 'Based on locale', value: 'locale' },
      ],
      filter: null,
    }),
    isRequired,
    Section('Data Handling', name, path, formId, defaultValue, preFill),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: { type: 'text', format: 'YYYY-MM-DD', isRequired: false, preFill: true, alignment: 'left' },
  },
})

export const Discount = Component('Discount', {
  description: 'Allows user to provide promo code to apply discount',
  category: 'Cart & Checkout',
  fields: [
    OptionEditor({
      name: 'options',
      label: 'Discount Options',
      columns: [
        { label: 'Code', name: 'label' },
        {
          label: 'Value',
          name: 'value',
          type: 'double',
          tooltip:
            'Use values between 0.0 and 1.0 for percentage discount. Values over 1.0 mean absolute discount. Eg. 0.3 will translate to 30% and 50 to $50 discount.',
        },
      ],
    }),
    Textfield({
      name: 'codePath',
      label: 'Code storage path',
      tooltip: TOOLTIPS.PATH,
    }),
    Textfield({
      name: 'valuePath',
      label: 'Value storage path',
      tooltip: TOOLTIPS.PATH,
    }),
    Section(
      'Texts',
      Textfield({
        name: 'fieldLabel',
        label: 'Text field label',
      }),
      Textfield({
        name: 'buttonLabel',
        label: 'Button label',
      }),
      Textfield({
        name: 'successMessage',
        label: 'Success message',
      }),
      Textfield({
        name: 'errorMessage',
        label: 'Error message',
      })
    ),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: {
      options: [
        { label: 'Promo10', value: 0.1 },
        { label: 'Promo20', value: 0.2 },
        { label: 'Promo30', value: 0.3 },
      ],
      codePath: 'commerce.promotionID',
      fieldLabel: 'Discount code',
      buttonLabel: 'Apply',
      successMessage: 'Discount was applied',
      errorMessage: 'Discount code is invalid',
      alignment: 'left',
    },
  },
})

export const Divider = Component('Divider', {
  description: 'Displays a visual divider, mostly horizontal.',
  fields: [
    Dropdown({
      name: 'size',
      label: 'Size',
      options: [
        { label: 'S', value: 'S' },
        { label: 'M', value: 'M' },
        { label: 'L', value: 'L' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'orientation',
      label: 'Orientation',
      options: [
        { label: 'Horizontal', value: 'horizontal' },
        { label: 'Vertical', value: 'vertical' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'length',
      label: 'Length',
      options: [
        { label: 'S', value: 'S' },
        { label: 'M', value: 'M' },
        { label: 'L', value: 'L' },
        { label: 'XL', value: 'XL' },
      ],
      filter: null,
    }),
    alignment,
  ],
  defaultContent: {
    content: { size: 'L', orientation: 'horizontal', length: 'M', alignment: 'center' },
  },
})

export const ExperienceDecisioning = Component('ExperienceDecisioning', {
  description:
    'Renders personalization content from Adobe Journey Optimizer by surface. Choose Ribbon, Banner, or Card and optionally fix ribbon to top of page.',
  category: 'General',
  fields: [
    Textfield({
      name: 'surface',
      label: 'Surface',
      tooltip: 'Surface, e.g. web://dsn.adobe.com/luma3#TopRibbon',
    }),
    Dropdown({
      name: 'renderAs',
      label: 'Render as',
      options: [
        { label: 'Ribbon', value: 'ribbon' },
        { label: 'Banner', value: 'banner' },
        { label: 'Card', value: 'card' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'ribbonPlacement',
      label: 'Ribbon position',
      options: [
        { label: 'Inline', value: 'inline' },
        { label: 'Top of page', value: 'topOfPage' },
      ],
      filter: null,
      hidden: (cmp) => cmp.content?.renderAs !== 'ribbon',
    }),
    {
      ...isFullWidth,
      hidden: (cmp) => cmp.content?.renderAs !== 'banner',
    },
    Dropdown({
      name: 'variant',
      label: 'Card layout',
      options: [
        { label: 'Image on top', value: 'vertical' },
        { label: 'Image on the left', value: 'horizontalLeft' },
        { label: 'Image on the right', value: 'horizontalRight' },
        { label: 'Image partially overlayed', value: 'partialOverlay' },
        { label: 'Image fully overlayed', value: 'fullOverlay' },
      ],
      filter: null,
      hidden: (cmp) => cmp.content?.renderAs !== 'card',
    }),
    Section(
      'Alignment',
      {
        ...alignment,
        hidden: (cmp) => !['banner', 'card'].includes(cmp.content?.renderAs),
      },
      {
        ...verticalAlignment,
        hidden: (cmp) => !['banner', 'card'].includes(cmp.content?.renderAs),
      }
    ),
  ],
  exposedStyles: [
    { label: 'Background colour', name: 'backgroundColor', placeholder: 'e.g. #ffffff' },
    {
      label: 'Height',
      name: 'height',
      placeholder: 'e.g. auto or 500px',
      hidden: (cmp) => cmp.content?.renderAs !== 'banner',
    },
    {
      label: 'Text colour',
      name: 'color',
      placeholder: 'e.g. #f9e322',
    },
  ],
  defaultContent: {
    content: {
      renderAs: 'ribbon',
      ribbonPlacement: 'topOfPage',
    },
  },
})

export const ExpandableSection = Component('ExpandableSection', {
  category: 'Layout',
  description: 'Displays a section with a title, that can be expanded and collapsed to show and hide its content.',
  fields: [Textfield({ name: 'label', label: 'Label' })],
  hasChildren: true,
  exposedStyles: [
    {
      label: 'Background',
      name: 'background',
      tooltip: "Color, gradient or image, eg: #fab31e, var(--primaryColor) or url('https://dsn.adobe.com/image.jpg')",
    },
    {
      label: 'Border Radius',
      name: 'borderRadius',
      tooltip: 'Radius for the border or background, eg. "1rem", "2rem", "6px", etc.',
    },
  ],
  defaultContent: {
    content: [],
  },
})

export const FAQ = Component('FAQ', {
  label: 'FAQ',
  description: 'Displays question/answer pairs as a list of expandable panels.',
  fields: [
    OptionEditor({
      name: 'options',
      label: 'Questions',
      columns: [
        { name: 'label', label: 'Question' },
        { name: 'text', label: 'Answer' },
      ],
    }),
    alignment,
  ],
  exposedStyles: [{ label: 'Gap', name: 'gap', placeholder: 'Eg. 0, 1rem or 16px' }],
  defaultContent: {
    content: {
      options: [
        {
          label: 'What types of payment methods do you accept on your website?',
          text: 'We accept online payments via credit card and PayPal for our services.',
        },
        {
          label: 'How can I track the status of my service request?',
          text: 'You can track the status of your service request by logging into your account and checking the service history page. Alternatively, you can contact our customer service team for assistance.',
        },
        {
          label: 'Do you offer refunds for your services?',
          text: 'We have a satisfaction guarantee policy that allows you to request a refund if you are not satisfied with our service. Please refer to our refund policy for more information and contact our customer service team to initiate a refund request.',
        },
      ],
      alignment: 'left',
    },
  },
})

export const FlightResults = Component('FlightResults', {
  category: 'Search',
  description:
    'Displays a list of products formatted as flights. Based on *from* and *to* properties in URL or dataLayer, as well as in products.',
  fields: [
    link,
    formId,
    Textfield({
      name: 'from',
      label: 'From (optional)',
      tooltip:
        'Leave blank to read this value from URL or search form. Setting it will cause the component to ignore other sources.',
    }),
    Textfield({
      name: 'to',
      label: 'To (optional)',
      tooltip:
        'Leave blank to read this value from URL or search form. Setting it will cause the component to ignore other sources.',
    }),
    Section(
      'Labels',
      Textfield({ name: 'buttonLabel', label: 'Button Label' }),
      RichText({ name: 'emptyOptionLabel', label: 'Unavailable option label' }),
      RichText({ name: 'noResultsLabel', label: 'No result text' })
    ),
    Section('Actions', eventType, webhookUrl, data),
    alignment,
  ],
  defaultContent: {
    content: {
      formId: 'flight',
      buttonLabel: 'Select',
      emptyOptionLabel: '<em>Not available</em>',
      noResultsLabel: "<p>We're sorry. We didn't find any flights matching your criteria.</p>",
    },
  },
})

export const FlightSearch = Component('FlightSearch', {
  category: 'Search',
  description:
    'Displays a simple flight search component with *from* and *to* fields as well as date and additional options.',
  fields: [
    link,
    Dropdown({
      name: 'type',
      label: 'Input type',
      options: [
        { label: 'Picker (from products)', value: 'automaticPicker' },
        { label: 'Picker (user-defined)', value: 'picker' },
        { label: 'Text field', value: '' },
      ],
      filter: null,
    }),
    OptionEditor({
      name: 'locations',
      label: 'Locations',
      columns: [
        { name: 'label', label: 'Label' },
        { name: 'value', label: 'Value' },
      ],
      saveAs: 'array',
      hidden: (cmp) => cmp.content.type !== 'picker',
    }),
    formId,
    Textfield({
      name: 'from',
      label: 'From (optional)',
      tooltip: 'Default if no other value is found in dataLayer and URL.',
    }),
    Textfield({
      name: 'to',
      label: 'To (optional)',
      tooltip: 'Default if no other value is found in dataLayer and URL.',
    }),
    OptionEditor({
      name: 'options',
      label: 'Additional Options',
      columns: [
        { label: 'Label', name: 'label', type: 'text' },
        { label: 'Value', name: 'value', type: 'text' },
      ],
    }),
    Section(
      'Labels',
      Textfield({ name: 'fromLabel', label: 'From Label' }),
      Textfield({ name: 'toLabel', label: 'To Label' }),
      Textfield({ name: 'dateLabel', label: 'Date Label' }),
      Textfield({ name: 'buttonLabel', label: 'Button Label' })
    ),
    Section(
      'Actions',
      Textfield({
        name: 'path',
        label: 'Options storage path',
        tooltip: TOOLTIPS.PATH,
      }),
      eventType,
      webhookUrl,
      data,
      _Switch({
        name: 'shouldSaveSeparately',
        label: 'Save options separately',
        tooltip:
          'By default (not checked) selected options are saved at *path* as an array. If checked, each selected option will be saved at *path*.*value* as true/false.',
        defaultChecked: false,
      })
    ),
    alignment,
  ],
  defaultContent: {
    content: {
      type: 'automaticPicker',
      shouldSaveSeparately: false,
      formId: 'flight',
      fromLabel: 'From',
      toLabel: 'To',
      dateLabel: 'Date',
      buttonLabel: 'Search',
    },
  },
})

export const GeneratedText = Component('GeneratedText', {
  description:
    'A text component that generates an ID string based on provided pattern. Generated text can be stored and loaded similar to value of input components.',
  fields: [
    Textfield({
      name: 'pattern',
      label: 'Pattern',
      tooltip:
        'Pattern for generating the ID. Available symbols: * => digit or letter, # => digit, @ => lower case letter, ^ => upper case letter, $ => digit or lower case letter, % => digit or upper case letter. Any other character and white-space will be inserted literally.',
    }),
    name,
    path,
    formId,
    preFill,
    alignment,
  ],
  defaultContent: {
    content: {
      pattern: '******',
      preFill: true,
      alignment: 'left',
    },
  },
})

export const GridContainer = Component('GridContainer', {
  description: 'Container for organizing components into a defined grid. Use Styles to define rows and columns.',
  category: 'Layout',
  fields: [isFullWidth],
  exposedStyles: [
    { label: 'Columns', name: 'gridTemplateColumns', placeholder: 'eg. repeat(4, auto)' },
    { label: 'Column gap', name: 'columnGap', placeholder: 'eg. 1rem or 0' },
    { label: 'Rows', name: 'gridTemplateRows', placeholder: 'eg. repeat(4, auto)' },
    { label: 'Row gap', name: 'rowGap', placeholder: 'eg. 1rem or 0' },
    {
      label: 'Background',
      name: 'background',
      tooltip: "Color, gradient or image, eg: #fab31e, var(--primaryColor) or url('https://dsn.adobe.com/image.jpg')",
    },
    {
      label: 'Background Repeat',
      name: 'backgroundRepeat',
      tooltip:
        'If background is an image, should it be displayed in a repeating pattern or not. Available options: repeat, no-repeat, repeat-x, repeat-y, space, round.',
    },
    {
      label: 'Background Size',
      name: 'backgroundSize',
      tooltip:
        'If background is an image, should it be resized to match size. Available options: "cover", "contain", or values for each axis, eg. "50% 100%", "100% 150px", "300px 200px", etc.',
    },
  ],
  hasChildren: true,
  defaultContent: {
    meta: {
      isFullWidth: false,
    },
    content: [],
    styles: {
      gridTemplateColumns: 'repeat(4, auto)',
      gridTemplateRows: 'repeat(4, auto)',
      columnGap: '1rem',
      rowGap: '1rem',
    },
  },
})

export const Iframe = Component('Iframe', {
  description: 'Renders iframe based on URL. Can be used to display Adobe Sign web forms.',
  fields: [Textfield({ name: 'url', label: 'URL' }), title],
  defaultContent: {
    content: { text: 'Lorem Ipsum dolor sit amet...' },
  },
})

export const Image = Component('Image', {
  description: 'Displays an image.',
  fields: [
    _Image({ name: 'src', label: 'Image' }),
    link,
    Textfield({ name: 'alt', label: 'Alt text' }),
    Dropdown({
      name: 'objectFit',
      label: 'Image fit',
      options: [
        { label: 'fill', value: 'fill' },
        { label: 'contain', value: 'contain' },
        { label: 'cover', value: 'cover' },
        { label: 'none', value: 'none' },
        { label: 'scale-down', value: 'scale-down' },
        { label: 'inherit', value: 'inherit' },
        { label: 'initial', value: 'initial' },
        { label: 'unset', value: 'unset' },
      ],
      filter: null,
    }),
  ],
  exposedStyles: [{ label: 'Height', name: 'height', placeholder: 'eg. auto or 300px' }],
  defaultContent: {
    content: { objectFit: 'contain' },
  },
})

export const ImageGallery = Component('ImageGallery', {
  description:
    'Displays a set of images in a horizontal carousel with optional controls, autoplay and tumbnail preview.',
  fields: [
    OptionEditor({
      name: 'images',
      label: 'Slides',
      columns: [
        { name: 'original', label: 'Original URL*' },
        {
          name: 'thumbnail',
          label: 'Thumbnail URL (optional)',
          tooltip:
            'It is recommended to provide a small-size thumbnail for navigation. If left empty, original image will be used. Large, original files used as thumbnails can negatively impact performance and load time,',
        },
        { name: 'text', label: 'Text' },
        { name: 'buttonLabel', label: 'Button Label' },
        {
          name: 'buttonLink',
          label: 'Button Link',
          tooltip: 'Screen name (eg. home, sign-in, etc.) or a full URL (eg. https://adobe.com)',
        },
      ],
    }),
    _Switch({ name: 'showNav', label: 'Show controls', defaultChecked: true }),
    _Switch({ name: 'showPlay', label: 'Show play button', defaultChecked: true }),
    _Switch({ name: 'showFullScreen', label: 'Show fullscreen button', defaultChecked: true }),
    _Switch({ name: 'autoPlay', label: 'Auto-play', defaultChecked: false }),
    _Switch({ name: 'showThumbnails', label: 'Show thumbnails', defaultChecked: true }),
    Dropdown({
      name: 'thumbnailPosition',
      label: 'Thumbnail position',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
        { label: 'Top', value: 'top' },
        { label: 'Bottom', value: 'bottom' },
      ],
      filter: null,
      hidden: (cmp) => !cmp.content.showThumbnails,
    }),
    Dropdown({
      name: 'detailsPosition',
      label: 'Details position',
      options: [
        { label: 'Hidden', value: 'hidden' },
        { label: 'Bottom', value: 'bottom' },
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
      ],
      filter: null,
    }),
  ],
  exposedStyles: [
    { label: 'Width', name: 'width', placeholder: 'eg. 100% or 500px' },
    { label: 'Gallery Height', name: 'height', placeholder: 'eg. 400px or 12rem' },
  ],
  defaultContent: {
    content: {
      thumbnailPosition: 'left',
      showNav: true,
      showPlay: true,
      showFullScreen: true,
      autoPlay: false,
      showThumbnails: true,
      detailsPosition: 'hidden',
      images: [
        {
          original: 'https://picsum.photos/id/1018/1000/600/',
          thumbnail: 'https://picsum.photos/id/1018/250/150/',
        },
        {
          original: 'https://picsum.photos/id/1015/1000/600/',
          thumbnail: 'https://picsum.photos/id/1015/250/150/',
        },
        {
          original: 'https://picsum.photos/id/1019/1000/600/',
          thumbnail: 'https://picsum.photos/id/1019/250/150/',
        },
      ],
    },
  },
})

export const List = Component('List', {
  label: 'Horizontal Container',
  description: 'Responsive container for creating horizontal lists.',
  category: 'Layout',
  fields: [
    Dropdown({
      name: 'layout',
      label: 'Overflowing items',
      options: [
        { label: 'Float', value: 'float' },
        { label: 'Wrap', value: 'wrap' },
        { label: 'Scroll', value: 'scroll' },
        { label: 'Fit', value: 'fit' },
      ],
      hidden: null,
      filter: null,
    }),
    Dropdown({
      name: 'itemAlignment',
      label: 'Item Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
      hidden: null,
      filter: null,
    }),
    isFullWidth,
  ],
  exposedStyles: [
    { label: 'Gap', name: 'gap', placeholder: 'eg. 2rem or 16px' },
    {
      label: 'Background',
      name: 'background',
      tooltip: "Color, gradient or image, eg: #fab31e, var(--primaryColor) or url('https://dsn.adobe.com/image.jpg')",
    },
    {
      label: 'Background Repeat',
      name: 'backgroundRepeat',
      tooltip:
        'If background is an image, should it be displayed in a repeating pattern or not. Available options: repeat, no-repeat, repeat-x, repeat-y, space, round.',
    },
    {
      label: 'Background Size',
      name: 'backgroundSize',
      tooltip:
        'If background is an image, should it be resized to match size. Available options: "cover", "contain", or values for each axis, eg. "50% 100%", "100% 150px", "300px 200px", etc.',
    },
  ],
  hasChildren: true,
  defaultContent: {
    meta: {
      layout: 'wrap',
      isFullWidth: false,
      itemAlignment: 'left',
    },
    content: [],
  },
})

export const LoginForm = Component('LoginForm', {
  description: 'Displays a login form with registration option.',
  fields: [
    link,
    Textfield({
      name: 'buttonLabel',
      label: 'Button label',
      tooltip: 'Text on the sign-in button.',
    }),
    _Switch({
      name: 'hidePasswordField',
      label: 'Hide password field',
      defaultChecked: false,
    }),
    Section(
      'Registration',
      Custom({
        name: 'registrationNote',
        render: ({ component, projectId }) =>
          `<em>You can check and select a registration screen in project's <a href='/projects/${projectId}/navigation'>Navigation</a> tab.</em>`,
      }),
      _Switch({ name: 'hideRegistration', label: 'Hide registration link', defaultChecked: false }),
      Textfield({
        name: 'registrationLabel',
        label: 'Link text',
        tooltip: 'Text to be displayed as the registration link.',
      })
    ),
    Section(
      'Data Handling',
      Textfield({
        name: 'path',
        label: 'Email storage path',
        tooltip: TOOLTIPS.PATH,
      }),
      eventType,
      webhookUrl,
      formId
    ),
  ],
  defaultContent: {
    content: { path: 'personalEmail.address', buttonLabel: 'Sign in', registrationLabel: 'Create an account' },
  },
})

export const LoanCalculator = Component('LoanCalculator', {
  label: 'LoanCalculator',
  category: 'FSI',
  description: 'Displays a loan calculator with sliders to enter purchase price, down payment and loan term.',
  fields: [
    Textfield({
      name: 'title',
      label: 'Title',
    }),
    Textfield({
      name: 'buttonLabel',
      label: 'Button label',
    }),
    Screen({ name: 'buttonLink', label: 'Button link', filter: null }),
    Number({ name: 'interestRate', label: 'Interest rate', min: 0.0, max: 50.0 }),
    Section(
      'Price',
      Textfield({
        name: 'priceLabel',
        label: 'Price Label',
      }),
      Number({ name: 'priceMinValue', label: 'Minimum price', min: 1, max: 10000000 }),
      Number({ name: 'priceMaxValue', label: 'Maximum price', min: 1, max: 10000000 })
    ),
    Section(
      'Down payment',
      Textfield({
        name: 'downPaymentLabel',
        label: 'Down Payment Label',
      }),
      Number({ name: 'downPaymentMinValue', label: 'Minimum down payment', min: 0, max: 10000000 }),
      Number({ name: 'downPaymentMaxValue', label: 'Maximum down payment', min: 0, max: 10000000 })
    ),
    Section(
      'Term',
      Textfield({
        name: 'termLabel',
        label: 'Term Label',
      }),
      Number({ name: 'termMinValue', label: 'Minimum term', min: 1, max: 30 }),
      Number({ name: 'termMaxValue', label: 'Maximum term', min: 1, max: 30 }),
      Textfield({
        name: 'termUnit',
        label: 'Term Unit',
      })
    ),
    Section('Actions', eventType, webhookUrl),
    Section(
      'Other labels',
      Textfield({
        name: 'monthlyPaymentLabel',
        label: 'Monthly Payment Label',
      }),
      Textfield({
        name: 'disclaimer',
        label: 'Disclaimer',
      })
    ),
  ],
  defaultContent: {
    content: {
      title: 'Loan Calculator',
      priceMinValue: 1000,
      priceMaxValue: 2500000,
      downPaymentMinValue: 0,
      downPaymentMaxValue: 1000000,
      termMinValue: 1,
      termMaxValue: 30,
      termUnit: 'years',
      interestRate: 6.15,
      buttonLabel: 'Apply now',
      priceLabel: 'Purchase price',
      downPaymentLabel: 'Down payment',
      termLabel: 'Term',
      yearsLabel: 'years',
      monthlyPaymentLabel: 'Monthly payment',
      disclaimer: 'Estimate how much you could be paying monthly for your loan',
    },
  },
})

export const MortgageCalculator = Component('MortgageCalculator', {
  label: 'Mortgage Calculator',
  category: 'FSI',
  description: 'Deprecated, please use Loan Calculator component instead.',
  fields: [
    Textfield({
      name: 'title',
      label: 'Title',
    }),
    Number({ name: 'minPrice', label: 'Minimum price', min: 1, max: 10000000 }),
    Number({ name: 'maxPrice', label: 'Maximum price', min: 1, max: 10000000 }),
    Number({ name: 'interestRate', label: 'Interest rate', min: 0.0, max: 50.0 }),
    Textfield({
      name: 'buttonLabel',
      label: 'Button label',
    }),
    Screen({ name: 'buttonLink', label: 'Button link', filter: null }),
    Section('Actions', eventType, webhookUrl),
    Section(
      'Labels',
      Textfield({
        name: 'priceLabel',
        label: 'Price Label',
      }),
      Textfield({
        name: 'downPaymentLabel',
        label: 'Down Payment Label',
      }),
      Textfield({
        name: 'termLabel',
        label: 'Term Label',
      }),
      Textfield({
        name: 'yearsLabel',
        label: 'Years Label',
      }),
      Textfield({
        name: 'monthlyPaymentLabel',
        label: 'Monthly Payment Label',
      }),
      Textfield({
        name: 'disclaimer',
        label: 'Disclaimer',
      })
    ),
  ],
  defaultContent: {
    content: {
      title: 'Mortgage Calculator',
      minPrice: 25000,
      maxPrice: 2500000,
      interestRate: 6.0,
      buttonLabel: 'Apply now',
      priceLabel: 'Purchase price',
      downPaymentLabel: 'Down payment',
      termLabel: 'Term',
      yearsLabel: 'years',
      monthlyPaymentLabel: 'Monthly payment',
      disclaimer: 'Estimate how much you could be paying monthly for your mortgage',
    },
  },
})

export const OrderSummary = Component('OrderSummary', {
  description: 'Displays the summaryof current order, including discount, shipping, etc.',
  category: 'Cart & Checkout',
  fields: [
    Textfield({
      name: 'addressLabel',
      label: 'Address Label',
    }),
    RichText({ name: 'addressText', label: 'Address format' }),
    Textfield({
      name: 'shippingLabel',
      label: 'Shipping method label',
    }),
    Textfield({
      name: 'discountLabel',
      label: 'Discount label',
    }),
  ],
  defaultContent: {
    content: {
      addressLabel: 'Billing address',
      addressText:
        '<div><p>${person.name.firstName}&nbsp;${person.name.lastName}</p><p>${homeAddress.street1} ${homeAddress.postalCode} ${homeAddress.city}</p></div>',
      shippingLabel: 'Shipping',
      discountLabel: 'Discount',
    },
  },
})

export const Picker = Component('Picker', {
  label: 'Dropdown',
  category: 'Form',
  description: 'Displays one ore more options in drop-down picker.',
  fields: [label, placeholder, ...selectionFields],
  defaultContent: {
    content: { type: 'Picker', direction: 'vertical', alignment: 'left', preFill: true },
  },
})

export const ProductAddToCartButton = Component('ProductAddToCartButton', {
  description:
    'Displays a button with specified label and visual variant. Can link to other screen or trigger actions.',
  category: 'Product',
  fields: [
    productId,
    Products({
      name: 'additionalProducts',
      label: 'Additional Products',
      filter: null,
    }),
    label,
    link,
    Dropdown({ name: 'variant', label: 'Variant', options: BUTTON_VARIANTS, filter: null }),
    _Switch({ name: 'isQuiet', label: 'Is quiet', defaultChecked: false }),
    _Switch({ name: 'showQuantityControls', label: 'Show Quantity Controls', defaultChecked: false }),
    Section('Actions', eventType, webhookUrl, formId, data, toastVariant, toastMessage),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: {
      showQuantityControls: false,
      label: 'Add to cart',
      variant: 'cta',
      alignment: 'center',
      eventType: 'addToCart',
      toastVariant: 'success',
      toastMessage: 'Product added to cart',
    },
  },
})

export const ProductCard = Component('ProductCard', {
  description: 'Displays product information in the form of a card.',
  category: 'Product',
  fields: [
    productId,
    _Switch({ name: 'hideName', label: 'Hide Name', defaultChecked: false }),
    _Switch({ name: 'hideDescription', label: 'Hide Description', defaultChecked: false }),
    _Switch({ name: 'hideCategory', label: 'Hide Category', defaultChecked: false }),
    Dropdown({
      name: 'ratingPosition',
      label: 'Rating Position',
      options: [
        { label: 'Hidden', value: 'hidden' },
        { label: 'Top', value: 'top' },
        { label: 'Middle', value: 'middle' },
        { label: 'Bottom', value: 'bottom' },
      ],
      filter: null,
    }),
    link,
    productCardVariants,
    Dropdown({ name: 'titleLevel', label: 'Title size', options: HEADING_LEVELS, filter: null }),
    buttonAppearance,
    buttonActions,
    Section('Alignment', alignment, verticalAlignment),
  ],
  defaultContent: {
    content: {
      titleLevel: 2,
      hideName: false,
      hideDescription: false,
      hideCategory: true,
      ratingPosition: 'top',
      variant: 'horizontalLeft',
    },
  },
})

export const ProductCategory = Component('ProductCategory', {
  description: 'Displays product category. If product is not selected, will use one linked to current screen.',
  category: 'Product',
  fields: [productId, RichText({ label: 'Default Value', name: 'defaultValue' }), alignment],
  defaultContent: {
    content: { alignment: 'left' },
  },
})

export const ProductDescription = Component('ProductDescription', {
  description: 'Displays product description. If product is not selected, will use one linked to current screen.',
  category: 'Product',
  fields: [productId, RichText({ label: 'Default Value', name: 'defaultValue' }), alignment],
  defaultContent: {
    content: { alignment: 'left' },
  },
})

export const ProductGallery = Component('ProductGallery', {
  category: 'Product',
  description:
    'Displays a gallery/carousel of product images with optional detail panel ontaining its description and a link to product screen.',
  fields: [
    Products({ name: 'products', label: 'Products', filter: null }),
    Screen({ name: 'link', label: 'Link to', allowExternal: true, allowProduct: false, filter: null }),
    Textfield({
      name: 'descriptionProperty',
      label: 'Description Property',
      tooltip:
        'Name of the product property to use for a details panel content. Usually `description` or `shortDescription`.',
    }),
    Textfield({
      name: 'buttonLabel',
      label: 'Button Label',
    }),
    Dropdown({
      name: 'detailsPosition',
      label: 'Details position',
      options: [
        { label: 'Hidden', value: 'hidden' },
        { label: 'Bottom', value: 'bottom' },
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
      ],
      filter: null,
    }),
    Section(
      'Slide Appearance',
      _Switch({ name: 'showNav', label: 'Show controls', defaultChecked: true }),
      _Switch({ name: 'showPlay', label: 'Show play button', defaultChecked: true }),
      _Switch({ name: 'showFullScreen', label: 'Show fullscreen button', defaultChecked: true }),
      _Switch({ name: 'autoPlay', label: 'Auto-play', defaultChecked: false }),
      _Switch({ name: 'showThumbnails', label: 'Show thumbnails', defaultChecked: true }),
      Dropdown({
        name: 'thumbnailPosition',
        label: 'Thumbnail position',
        options: [
          { label: 'Left', value: 'left' },
          { label: 'Right', value: 'right' },
          { label: 'Top', value: 'top' },
          { label: 'Bottom', value: 'bottom' },
        ],
        filter: null,
        hidden: (cmp) => !cmp.content.showThumbnails,
      })
    ),
  ],
  exposedStyles: [
    { label: 'Width', name: 'width', placeholder: 'eg. 100% or 500px' },
    { label: 'Gallery Height', name: 'height', placeholder: 'eg. 400px or 12rem' },
  ],
  defaultContent: {
    content: {
      descriptionProperty: 'shortDescription',
      buttonLabel: 'View',
      detailsPosition: 'hidden',
      thumbnailPosition: 'left',
      showNav: true,
      showPlay: false,
      showFullScreen: false,
      autoPlay: false,
      showThumbnails: false,
    },
    styles: {
      height: '400px',
    },
  },
})

export const ProductImage = Component('ProductImage', {
  description: 'Displays product image. If product is not selected, will use one linked to current screen.',
  category: 'Product',
  fields: [productId, _Image({ label: 'Default Image', name: 'defaultValue' }), alignment],
  defaultContent: {
    content: { alignment: 'left' },
  },
})

export const ProductList = Component('ProductList', {
  description:
    'Displays list of products in selected category. Can be used inside Grid Container to better control layout.',
  category: 'Product',
  fields: [
    Textfield({
      label: 'Filter property',
      name: 'filterProperty',
      tooltip: 'Name of product property that will be used for filtering, default: "category"',
    }),
    Textfield({
      label: 'Filter value',
      name: 'filter',
      tooltip: 'Regular expression that will be used for filtering products',
    }),
    Number({ name: 'limit', label: 'Limit', min: 0, max: 100 }),
    Dropdown({
      name: 'sort',
      label: 'Sort by',
      options: [
        { label: 'Name (A-Z)', value: 'nameAsc' },
        { label: 'Price (low to high)', value: 'priceAsc' },
        { label: 'Price (high to low)', value: 'priceDesc' },
        { label: 'Random', value: 'random' },
      ],
      filter: null,
    }),
    Section(
      'List items',
      { ...link, label: 'Item link', name: 'itemLink' },
      { ...productCardVariants, label: 'Item variant', name: 'itemVariant' },
      { ...alignment, label: 'Item alignment', name: 'itemAlignment' }
    ),
  ],
  defaultContent: {
    content: { filterProperty: 'category', alignment: 'left', itemVariant: 'vertical', itemAlignment: 'center' },
  },
})

export const ProductName = Component('ProductName', {
  description: 'Displays product name. If product is not selected, will use one linked to current screen.',
  category: 'Product',
  fields: [
    productId,
    defaultValue,
    Dropdown({
      name: 'displayAs',
      label: 'Display as',
      options: DISPLAY_OPTIONS,
      filter: null,
    }),
    alignment,
  ],
  defaultContent: {
    content: { displayAs: 2, alignment: 'left' },
  },
})

export const ProductPrice = Component('ProductPrice', {
  description: 'Displays product price. If product is not selected, will use one linked to current screen.',
  category: 'Product',
  fields: [productId, defaultValue, alignment],
  defaultContent: {
    content: { alignment: 'left' },
  },
})

export const ProductProperty = Component('ProductProperty', {
  description: 'Displays selected product property. If product is not selected, will use one linked to current screen.',
  category: 'Product',
  fields: [
    productId,
    Textfield({ label: 'Property name', name: 'propertyName' }),
    defaultValue,
    Dropdown({
      name: 'displayAs',
      label: 'Display as',
      options: DISPLAY_OPTIONS,
      filter: null,
    }),
    _Switch({
      label: 'Format as price',
      name: 'formatAsPrice',
      defaultChecked: false,
      hidden: (cmp) => ['image', 'video', 'stockStatus'].includes(cmp.content.displayAs),
    }),
    alignment,
  ],
  defaultContent: {
    content: { propertyName: 'name', displayAs: 'p', alignment: 'left' },
  },
})

export const ProductStockStatus = Component('ProductStockStatus', {
  description: 'Displays selected product property. If product is not selected, will use one linked to current screen.',
  category: 'Product',
  fields: [
    productId,
    Textfield({ label: 'Default value', name: 'defaultValue' }),
    Textfield({ label: 'In-stock label', name: 'inStockLabel' }),
    Textfield({ label: 'Out-of-stock label', name: 'outOfStockLabel' }),
    alignment,
  ],
  defaultContent: {
    content: {
      propertyName: 'stock_status',
      displayAs: 'stockStatus',
      defaultValue: 'IN_STOCK',
      inStockLabel: 'In stock',
      outOfStockLabel: 'Out of stock',
      alignment: 'left',
    },
  },
})

export const ProductSelection = Component('ProductSelection', {
  category: 'Product',
  description: 'Displays several products as options to choose from.',
  fields: [
    Products({ name: 'productIds', label: 'Products', filter: null }),
    Dropdown({
      name: 'display',
      label: 'Display as',
      options: [
        { label: 'Large tiles', value: 'largeTiles' },
        { label: 'Small tiles', value: 'smallTiles' },
        { label: 'Small tiles with preview', value: 'smallTilesPreview' },
      ],
      filter: null,
    }),
    _Switch({ name: 'allowMultiple', label: 'Allow multiple', defaultChecked: false }),
    path,
    formId,
    Section(
      'Data Source',
      Textfield({ name: 'thumbnailProperty', label: 'Thumbnail property' }),
      Textfield({ name: 'previewProperty', label: 'Preview image property' }),
      Textfield({ name: 'nameProperty', label: 'Product name property' })
    ),
  ],
  exposedStyles: [],
  defaultContent: {
    content: {
      productIds: [],
      display: 'smallTilesPreview',
      allowMultiple: false,
      thumbnailProperty: '',
      previewProperty: 'image',
      nameProperty: 'name',
    },
  },
})

export const ProductVideo = Component('ProductVideo', {
  description: 'Displays product video. If product is not selected, will use one linked to current screen.',
  category: 'Product',
  fields: [
    productId,
    _Video({ label: 'Default Video', name: 'defaultValue' }),
    _Radio({
      name: 'mode',
      label: 'Playback mode',
      options: [
        { label: 'Automatic', value: 'automatic', defaultChecked: true },
        { label: 'User-controlled', value: 'user' },
      ],
    }),
    _Switch({
      name: 'loop',
      label: 'Loop',
      defaultChecked: false,
      tooltip: 'Loop the video playback.',
      hidden: (cmp) => cmp.content.mode === 'user',
    }),
    Section(
      'Actions',
      Textfield({ name: 'startedEventType', label: 'Started event type' }),
      Textfield({ name: 'startedWebhookUrl', label: 'Started Webhook URL' }),
      Textfield({ name: 'endedEventType', label: 'Ended event type' }),
      Textfield({ name: 'endedWebhookUrl', label: 'Ended Webhook URL' })
    ),
    alignment,
  ],
  exposedStyles: [
    { label: 'Width', name: 'width', placeholder: 'eg. auto or 640px' },
    { label: 'Height', name: 'height', placeholder: 'eg. auto or 360px' },
  ],
  defaultContent: {
    content: { alignment: 'center', mode: 'user' },
  },
})

export const Progress = Component('Progress', {
  description: 'Displays a process progress indicator.',
  fields: [
    Number({ name: 'steps', label: 'Steps', min: 1, max: 10 }),
    Number({ name: 'currentStep', label: 'Current step', min: 1, max: 10 }),
    _Switch({ name: 'showLabel', label: 'Show label', defaultChecked: false }),
    Dropdown({
      name: 'visualisation',
      label: 'Visualisation',
      options: [
        { label: 'Dots', value: 'dots' },
        { label: 'Bar', value: 'bar' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'labelPosition',
      label: 'Label position',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Top', value: 'top' },
        { label: 'Right', value: 'right' },
        { label: 'Bottom', value: 'bottom' },
      ],
      filter: null,
    }),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: {
      showLabel: true,
      alignment: 'center',
      steps: 3,
      currentStep: 1,
      labelLocation: 'right',
    },
  },
})

export const Radio = Component('Radio', {
  description: 'Displays one ore more options as radio buttons.',
  category: 'Form',
  fields: [label, ...selectionFields],
  defaultContent: {
    content: { type: 'Radio', direction: 'vertical', alignment: 'left', preFill: true, shouldSaveSeparately: false },
  },
})

export const Rating = Component('Rating', {
  description: 'Displays a 0-5 start rating.',
  fields: [productId, defaultValue, alignment],
  defaultContent: {
    content: { defaultValue: 4.4 },
  },
})

export const RssAuthor = Component('RssAuthor', {
  label: 'Article - Author',
  description: 'Displays the author of RSS article used on current page',
  category: 'RSS',
  fields: [alignment],
  defaultContent: {
    content: { alignment: 'left' },
  },
})

export const RssBody = Component('RssBody', {
  label: 'Article - Body',
  description: 'Displays the body of RSS article used on current page',
  category: 'RSS',
  fields: [alignment],
  exposedStyles: [
    { label: 'Minimum width', name: 'minWidth', placeholder: 'eg. auto or 380px' },
    { label: 'Maximum width', name: 'maxWidth', placeholder: 'eg. auto or 800px' },
  ],
  defaultContent: {
    content: { alignment: 'left' },
    styles: { minWidth: '380px', maxWidth: '800px' },
  },
})

export const RssDate = Component('RssDate', {
  label: 'Article - Date',
  description: 'Displays the publication date of RSS article used on current page',
  category: 'RSS',
  fields: [
    Dropdown({ name: 'dateFormat', label: 'Date Format', options: DATE_FORMAT_OPTIONS, filter: null }),
    Dropdown({ name: 'timeFormat', label: 'Time Format', options: TIME_FORMAT_OPTIONS, filter: null }),
    alignment,
  ],
  defaultContent: {
    content: { dateFormat: 'local', timeFormat: 'local', alignment: 'left' },
  },
})

export const RssFeed = Component('RssFeed', {
  label: 'Feed - List',
  description: 'Displays RSS feed items in selected number and layout.',
  category: 'RSS',
  fields: [
    Textfield({ name: 'url', label: 'URL' }),
    Dropdown({ name: 'linkType', label: 'On click', options: RSS_LINK_TYPE_OPTIONS, filter: null }),
    Textfield({ name: 'link', label: 'Link', hidden: (cmp) => cmp.content.linkType !== 'link' }),
    Screen({
      name: 'link',
      label: 'Link to',
      filter: null,
      allowExternal: false,
      hidden: (cmp) => cmp.content.linkType !== 'screen',
      tooltip: TOOLTIPS.LINK,
    }),
    Number({ name: 'startIndex', label: 'Starting offset', min: 0, max: 99 }),
    Number({
      name: 'maxCount',
      label: 'Max count',
      min: 1,
      max: 99,
    }),
    rssMappingInput,
    Section(
      'Appearance',
      Dropdown({
        name: 'cardVariant',
        label: 'Article card layout',
        options: [
          { label: 'Image on top', value: 'vertical' },
          { label: 'Image on the left', value: 'horizontalLeft' },
          { label: 'Image on the right', value: 'horizontalRight' },
          { label: 'Image partially overlayed', value: 'partialOverlay' },
          { label: 'Image fully overlayed', value: 'fullOverlay' },
        ],
        hidden: null,
        filter: null,
      }),
      Dropdown({
        name: 'orientation',
        label: 'List Orientation',
        options: [
          { label: 'Horizontal', value: 'horizontal' },
          { label: 'Vertical', value: 'vertical' },
        ],
        hidden: null,
        filter: null,
      }),
      Textfield({
        name: 'cardWidth',
        label: 'Card width',
        tooltip: 'Width of each article card expresed in valid CSS value, eg. 200px, 20%, 3rem, etc.',
      }),
      Dropdown({ name: 'dateFormat', label: 'Date Format', options: DATE_FORMAT_OPTIONS, filter: null }),
      Dropdown({ name: 'timeFormat', label: 'Time Format', options: TIME_FORMAT_OPTIONS, filter: null }),
      alignment
    ),
  ],
  exposedStyles: [
    { label: 'Column gap', name: 'columnGap', placeholder: 'eg. 2rem or 16px' },
    { label: 'Row gap', name: 'rowGap', placeholder: 'eg. 1rem or 8px' },
  ],
  defaultContent: {
    content: {
      url: 'https://www.theguardian.com/world/rss',
      startIndex: 0,
      maxCount: 10,
      linkType: '',
      mapping: [
        { from: 'media:content', to: 'thumbnail', keepArray: true, selector: '.0.$.url' },
        { from: 'media:content', to: 'image', keepArray: true, selector: '.1.$.url' },
        { from: 'title', to: 'title' },
        { from: 'description', to: 'description' },
        { from: 'pubDate', to: 'date' },
        { from: 'link', to: 'link' },
      ],
      dateFormat: 'local',
      timeFormat: 'local',
      alignment: 'left',
    },
    styles: { columnGap: '1rem', rowGap: '1rem' },
  },
})

export const RssMainImage = Component('RssMainImage', {
  label: 'Article - Main Image',
  description: 'Displays the main image of RSS article used on current page',
  category: 'RSS',
  fields: [
    Dropdown({
      name: 'objectFit',
      label: 'Image fit',
      options: [
        { label: 'fill', value: 'fill' },
        { label: 'contain', value: 'contain' },
        { label: 'cover', value: 'cover' },
        { label: 'none', value: 'none' },
        { label: 'scale-down', value: 'scale-down' },
        { label: 'inherit', value: 'inherit' },
        { label: 'initial', value: 'initial' },
        { label: 'unset', value: 'unset' },
      ],
      filter: null,
    }),
    alignment,
  ],
  exposedStyles: [{ label: 'Height', name: 'height', placeholder: 'eg. auto or 500px' }],
  defaultContent: {
    content: {
      cardVariant: 'vertical',
      cardWidth: '184px',
      orientation: 'horizontal',
      alignment: 'left',
    },
  },
})

export const RssMainArticle = Component('RssMainArticle', {
  label: 'Feed - Main Article',
  description: 'Displays first item on RSS feed as a banner.',
  category: 'RSS',
  fields: [
    Textfield({ name: 'url', label: 'URL' }),
    Dropdown({ name: 'linkType', label: 'On click', options: RSS_LINK_TYPE_OPTIONS, filter: null }),
    Textfield({ name: 'link', label: 'Link', hidden: (cmp) => cmp.content.linkType !== 'link' }),
    Screen({
      name: 'link',
      label: 'Link to',
      filter: null,
      allowExternal: false,
      hidden: (cmp) => cmp.content.linkType !== 'screen',
      tooltip: TOOLTIPS.LINK,
    }),
    rssMappingInput,
    Dropdown({ name: 'dateFormat', label: 'Date Format', options: DATE_FORMAT_OPTIONS, filter: null }),
    Dropdown({ name: 'timeFormat', label: 'Time Format', options: TIME_FORMAT_OPTIONS, filter: null }),
    alignment,
  ],
  exposedStyles: [{ label: 'Row gap', name: 'rowGap', placeholder: 'eg. 1rem or 8px' }],
  defaultContent: {
    content: {
      url: 'https://www.theguardian.com/world/rss',
      linkType: '',
      mapping: [
        { from: 'media:content', to: 'thumbnail', keepArray: true, selector: '.0.$.url' },
        { from: 'media:content', to: 'image', keepArray: true, selector: '.1.$.url' },
        { from: 'title', to: 'title' },
        { from: 'description', to: 'description' },
        { from: 'pubDate', to: 'date' },
        { from: 'link', to: 'link' },
      ],
      dateFormat: 'local',
      timeFormat: 'local',
      alignment: 'left',
    },
    styles: { columnGap: '1rem', rowGap: '1rem' },
  },
})

export const RssTitle = Component('RssTitle', {
  label: 'Article - Title',
  description: 'Displays the title of RSS article used on current page',
  category: 'RSS',
  fields: [
    Dropdown({
      name: 'level',
      label: 'Heading Level',
      options: HEADING_LEVELS,
      filter: null,
    }),
    Section('Alignment', alignment, textAlignment),
  ],
  defaultContent: {
    content: { level: 2, alignment: 'left', textAlignment: 'left' },
  },
})

export const Selection = Component('Selection', {
  description: 'Displays a range of Selection inputs like switches, checkboxes, radio buttons and drop-downs.',
  category: 'Form',
  fields: [
    label,
    Dropdown({
      name: 'type',
      label: 'Type',
      options: [
        { label: 'Box', value: 'Box' },
        { label: 'Card', value: 'Card' },
      ],
      filter: null,
    }),
    ...selectionFields,
  ],
  defaultContent: {
    content: { type: 'Checkbox', direction: 'vertical', alignment: 'left', preFill: true, shouldSaveSeparately: false },
  },
})

export const ShippingMethod = Component('ShippingMethod', {
  description: 'Displays a range of Selection inputs like switches, checkboxes, radio buttons and drop-downs.',
  category: 'Cart & Checkout',
  fields: [
    ShippingOptionsEditor({ label: 'Shipping Options', name: 'options' }),
    defaultValue,
    Textfield({
      name: 'methodPath',
      label: 'Method storag path',
      tooltip: TOOLTIPS.PATH,
    }),
    Textfield({
      name: 'costPath',
      label: 'Cost storage path',
      tooltip: TOOLTIPS.PATH,
    }),
  ],
  defaultContent: {
    content: {
      options: [
        { label: 'Standard: 5-14 business days', method: 'standardShipping', cost: 5 },
        { label: 'Ground: 3-7 business days', method: 'groundShipping', cost: 10 },
        { label: 'Priority: 2 business days', method: 'priorityShipping', cost: 15 },
        { label: 'Express: 1 business day', method: 'expressShipping', cost: 20 },
        { label: 'Next-day pickup', method: 'pickup', cost: 0 },
      ],
      defaultValue: 'standardShipping',
      methodPath: 'commerce.shipping.shippingMethod',
      costPath: 'commerce.shipping.shippingAmount',
      alignment: 'left',
    },
  },
})

export const StatePicker = Component('StatePicker', {
  description: 'Displays a dropdown list of all US states',
  category: 'Form',
  fields: [label, Section('Data Handling', name, path, formId, defaultValue, preFill)],
  defaultContent: { content: { type: 'StatePicker', label: 'State' } },
})

export const Switch = Component('Switch', {
  description: 'Displays one ore more options as checkboxes.',
  category: 'Form',
  fields: [
    label,
    _Switch({ name: 'defaultValue', label: 'Selected by default', defaultChecked: true }),
    isRequired,
    Section('Data Handling', name, path, formId, preFill),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: { type: 'Switch', direction: 'vertical', alignment: 'left', preFill: true },
  },
})

export const Text = Component('Text', {
  description: 'Displays a text paragraph or renders HTML.',
  fields: [text, alignment],
  defaultContent: {
    content: { text: 'Lorem Ipsum dolor sit amet...' },
  },
})

export const TextArea = Component('TextArea', {
  description: 'Displays a multi-line text input field.',
  category: 'Form',
  fields: [
    label,
    isRequired,
    Section('Data Handling', name, path, formId, defaultValue, preFill),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: { isRequired: false, preFill: true },
  },
})

export const TextField = Component('TextField', {
  description: 'Displays a text input field.',
  category: 'Form',
  fields: [
    label,
    Dropdown({
      name: 'type',
      label: 'Type',
      options: [
        { label: 'text', value: 'text' },
        { label: 'search', value: 'search' },
        { label: 'url', value: 'url' },
        { label: 'tel', value: 'tel' },
        { label: 'email', value: 'email' },
        { label: 'password', value: 'password' },
      ],
      filter: null,
    }),
    placeholder,
    isRequired,
    Section('Data Handling', name, path, formId, defaultValue, preFill),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: { type: 'text', isRequired: false, preFill: true },
  },
})

export const TextSubmit = Component('TextSubmit', {
  description: 'Displays a textfield with submit button next to it.',
  category: 'Form',
  fields: [
    placeholder,
    Section('Data Handling', name, path, formId, defaultValue, preFill),
    buttonAppearance,
    buttonActions,
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: { alertText: 'Thank you!' },
  },
})

export const TimeSlotPicker = Component('TimeSlotPicker', {
  description: 'Displays a simple calendar with appointment time slots.',
  category: 'Form',
  fields: [
    _Switch({ name: 'multiple', label: 'Select multiple', defaultChecked: false }),

    Section(
      'Options',
      Number({ name: 'days', label: 'Displayed days', min: 1, max: 7 }),
      MultiField({ name: 'dailyOptions', label: 'Daily Options' })
    ),
    Section('Data Handling', name, path, formId),
    Section('Actions', eventType, webhookUrl),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: {
      multiple: false,
      days: 3,
      dailyOptions: ['9 AM', '10 AM', '11 AM', '12 AM'],
      alignment: 'center',
    },
  },
})

export const Title = Component('Title', {
  description: 'Displays a title block with subtitle, pretitle as well as several sizing an alignment options.',
  fields: [
    title,
    subtitle,
    pretitle,
    Dropdown({
      name: 'level',
      label: 'Heading Level',
      options: HEADING_LEVELS,
      filter: null,
    }),
    Section('Alignment', alignment, textAlignment),
    link,
  ],
  defaultContent: {
    content: { title: 'Lorem Ipsum', level: 1, alignment: 'left', textAlignment: 'left' },
  },
})

export const Upload = Component('Upload', {
  description: 'Displays a file upload/image capture component.',
  category: 'Form',
  fields: [
    label,
    placeholder,
    Dropdown({
      name: 'variant',
      label: 'Variant',
      options: [
        { label: 'Link', value: 'link' },
        { label: 'CTA', value: 'cta' },
        { label: 'Primary', value: 'primary' },
        { label: 'Secondary', value: 'secondary' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'type',
      label: 'Type',
      options: [
        { label: 'Image capture', value: 'imageCapture' },
        { label: 'Image capture with upload', value: 'imageCaptureWithUpload' },
        { label: 'Image Upload', value: 'imageUpload' },
        { label: 'File Upload', value: 'fileUpload' },
      ],
      filter: null,
    }),
    _Switch({ name: 'showPreview', label: 'Show Preview', defaultChecked: true }),
    _Switch({ name: 'showProgress', label: 'Show Progress', defaultChecked: false }),
    Section('Data Handling', name, path, formId, preFill),
    Section('Actions', eventType, webhookUrl),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    content: {
      type: 'imageUpload',
      variant: 'cta',
      alignment: 'center',
      preFill: false,
      showPreview: true,
      showProgress: false,
    },
  },
})

export const Wizard = Component('Wizard', {
  description: 'Displays a multi-step wizard. Each panel should be created as a separate page and linked.',
  category: 'Form',
  fields: [
    Textfield({ name: 'name', label: 'Wizard Name' }),
    Textfield({ name: 'title', label: 'Wizard Title' }),
    Screens({ name: 'panels', label: 'Panels', filter: null }),
    formId,
    Section(
      'Actions | Started',
      Textfield({
        name: 'startedEventType',
        label: 'Started event type',
        tooltip: TOOLTIPS.EVENT_TYPE + ' Triggered on starting the first step',
      }),
      Textfield({ name: 'startedWebhookUrl', label: 'Started Webhook URL' })
    ),
    Section(
      'Actions | Step',
      Textfield({
        name: 'stepEventType',
        label: 'Step event type',
        tooltip: TOOLTIPS.EVENT_TYPE + ' Triggered on each step',
      }),
      Textfield({ name: 'stepWebhookUrl', label: 'Step Webhook URL' })
    ),
    Section(
      'Actions | Ended',
      Textfield({
        name: 'endedEventType',
        label: 'Ended event type',
        tooltip: TOOLTIPS.EVENT_TYPE + ' Triggered on completing the last step',
      }),
      Textfield({ name: 'endedWebhookUrl', label: 'Ended Webhook URL' }),
      Number({
        name: 'endedDelay',
        label: 'Delay',
        min: 0,
        max: 10000,
        tooltip: 'Number of milliseconds to wait until leaving the wizard',
      }),
      link
    ),
    Section(
      'Actions | Abandoned',
      Textfield({
        name: 'abandonedEventType',
        label: 'Abandoned event type',
        tooltip: TOOLTIPS.EVENT_TYPE + ' Triggered on leaving the wizard before the last step',
      }),
      Textfield({ name: 'abandonedWebhookUrl', label: 'Abandoned Webhook URL' })
    ),
    Section(
      'Progress',
      _Switch({ name: 'showProgress', label: 'Show Progress', defaultChecked: false }),
      _Switch({ name: 'showProgressLabel', label: 'Show Progress Label', defaultChecked: false }),
      Dropdown({
        name: 'progressPosition',
        label: 'Progress Position',
        options: [
          { label: 'Above', value: 'above' },
          { label: 'Top', value: 'top' },
          { label: 'Bottom', value: 'bottom' },
          { label: 'Below', value: 'below' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'progressVisualisation',
        label: 'Progress visualisation',
        options: [
          { label: 'Dots', value: 'dots' },
          { label: 'Bar', value: 'bar' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'progressLabelPosition',
        label: 'Progress label position',
        options: [
          { label: 'Left', value: 'left' },
          { label: 'Top', value: 'top' },
          { label: 'Right', value: 'right' },
          { label: 'Bottom', value: 'bottom' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'progressAlignment',
        label: 'Progress alignment',
        options: ALIGNMENT_OPTIONS,
        filter: null,
      })
    ),
  ],
  defaultContent: {
    content: {
      panels: [],
      showProgress: false,
      progressPosition: 'top',
      direction: 'horizontal',
      startedEventType: 'wizard-started',
      stepEventType: 'wizard-step',
      endedEventType: 'wizard-ended',
      progressVisualisation: 'dots',
      progressLabelPosition: 'top',
      progressAlignment: 'center',
    },
  },
})

export const Video = Component('Video', {
  description: 'Displays a video.',
  fields: [
    _Video({ name: 'url', label: 'URL' }),
    _Radio({
      name: 'mode',
      label: 'Playback mode',
      options: [
        { label: 'Automatic', value: 'automatic', defaultChecked: true },
        { label: 'User-controlled', value: 'user' },
      ],
    }),
    _Switch({
      name: 'loop',
      label: 'Loop',
      defaultChecked: false,
      tooltip: 'Loop the video playback.',
      hidden: (cmp) => cmp.content.mode === 'user',
    }),
    Section(
      'Actions',
      Textfield({ name: 'startedEventType', label: 'Started event type' }),
      Textfield({ name: 'startedWebhookUrl', label: 'Started Webhook URL' }),
      Textfield({ name: 'endedEventType', label: 'Ended event type' }),
      Textfield({ name: 'endedWebhookUrl', label: 'Ended Webhook URL' }),
      formId
    ),
  ],
  exposedStyles: [
    { label: 'Width', name: 'width', placeholder: 'eg. auto or 640px' },
    { label: 'Height', name: 'height', placeholder: 'eg. auto or 360px' },
  ],
  defaultContent: {
    content: { mode: 'user' },
  },
})

export const DEFAULT_COMPONENT = { meta: { component: 'Title' }, content: { title: 'Title', level: 1 } }
